import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep } from 'es-toolkit';

import { useQueryClient } from '@zg-rentals/react-query5';
import { Box, Button, Heading, Icon, Input, Label, Modal, Paragraph, Text } from '@zillow/constellation-10';
import { IconVideoCameraOutline } from '@zillow/constellation-icons';
import { useRentalManagerUserPropertyEditWebApiHandler_videoUrl } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useSelector } from '../../../selectors/useSelector';

import stringHelper from '../../../utils/stringHelper';

import PropertiesActions from '../../../actions/PropertiesActions';

import { useUpdateUnitVideoLink } from '../../../hooks/queries/video';

import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';

import type { ChangeEvent } from 'react';
import type { IAPIErrorResponse } from '../../../interfaces';

const LISTING = 'listing';
const UNIT = 'unit';

type Props = {
  type?: typeof LISTING | typeof UNIT;
  feedModelId?: string;
  floorPlanIndex?: number;
  unitIndexToEdit?: number;
  isDisabled?: boolean;
};

const AddVideoLinkModalTrigger = ({
  type = LISTING,
  feedModelId = '',
  floorPlanIndex,
  unitIndexToEdit,
  isDisabled,
}: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    properties: {
      currentProperty: { listingAlias, floorplans },
    },
  } = useSelector((state) => state);

  const [videoLink, setVideoLink] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: uploadVideoLink } = useRentalManagerUserPropertyEditWebApiHandler_videoUrl();
  const { mutate: uploadUnitVideoLink } = useUpdateUnitVideoLink(listingAlias);

  const handleSave = () => {
    if (stringHelper.isValidUrl(videoLink)) {
      const queryOptions = {
        onSuccess: () => {
          let dataToSave;

          if (type === LISTING) {
            dataToSave = { videoUrl: videoLink };
          } else if (type === UNIT) {
            const floorPlanCopy = cloneDeep(floorplans);
            // @ts-expect-error
            floorPlanCopy[floorPlanIndex].units[unitIndexToEdit].videoUrl = videoLink;

            dataToSave = { floorplans: floorPlanCopy };
          }

          dispatch(PropertiesActions.updateCurrentProperty(dataToSave));

          queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
        },
        onError: (err: IAPIErrorResponse) => {
          if (err?.error?.message) {
            setErrorMessage(err.error.message);
          }
        },
      };

      switch (type) {
        case LISTING:
          uploadVideoLink(
            { getParams: { listingAlias, videoUrl: videoLink } },
            {
              onSuccess: queryOptions.onSuccess,
              onError: (err) => queryOptions.onError(err as IAPIErrorResponse),
            },
          );
          break;

        case UNIT:
          uploadUnitVideoLink({ feedModelId, videoUrl: videoLink }, queryOptions);
          break;
      }
    } else {
      setErrorMessage('Link not valid');
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (errorMessage) {
      setErrorMessage(null);
    } else {
      setVideoLink(e.target.value);
    }
  };

  return (
    <Modal
      size={{ base: 'fullScreen', lg: 'md' }}
      trigger={
        <Button data-testid="add-video-url-button" size="sm" tone="brand" emphasis="outlined" disabled={isDisabled}>
          Add video
        </Button>
      }
      header={<Heading level={2}>Video</Heading>}
      body={
        <>
          <Box css={{ marginRight: '600' }}>
            <Icon size="lg" css={{ color: 'icon.accent.brand.hero' }} render={<IconVideoCameraOutline />} />
          </Box>
          <Heading level={4} css={{ marginBlock: '400' }}>
            Add a video link
          </Heading>
          <Paragraph css={{ marginBottom: '400' }}>
            {type === LISTING &&
              'We recommend adding a professional-quality video that highlights your property’s unit and amenity features.'}
            {type === UNIT && 'We recommend adding a professional-quality video that highlights this unit’s features.'}
          </Paragraph>
          <Label required={true}>Video link</Label>
          <Input data-testid="add-video-url-input" onChange={handleOnChange}></Input>
        </>
      }
      footer={
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text css={{ marginRight: '600', color: 'colors.text.express.empower.strong' }}>{errorMessage || ''}</Text>
          <Button data-testid="add-video-save-button" tone="brand" emphasis="filled" onClick={handleSave}>
            Save
          </Button>
        </Box>
      }
    />
  );
};

export default AddVideoLinkModalTrigger;
